import React, { useEffect, useState } from 'react';
import { Select, SelectItem } from '@/components/ui/AppSelect';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

export const Section: React.FC<{ title: string; actions?: React.ReactNode; children: React.ReactNode }> = ({ title, actions, children }) => (
    <section className="border-b border-gray-200 dark:border-nightBorder px-4 py-3">
        <div className="flex items-center justify-between mb-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-nightMuted">{title}</h3>
            {actions}
        </div>
        <div className="space-y-2">{children}</div>
    </section>
);

export const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-nightTextSecondary" title={hint}>
        <span className="w-20 shrink-0">{label}</span>
        <div className="flex-1 min-w-0 flex items-center gap-1">{children}</div>
    </label>
);

const inputCls =
    'w-full min-w-0 h-7 px-2 rounded border border-gray-300 bg-white text-xs text-gray-900 focus:outline-none focus:border-primary40 focus:ring-1 focus:ring-primary40 dark:bg-nightElevated dark:border-nightBorder dark:text-nightTextSecondary dark:focus:border-nightAccent dark:focus:ring-nightAccent';

export const TextInput: React.FC<{
    value: string;
    onCommit: (v: string) => void;
    placeholder?: string;
    live?: boolean;
    disabled?: boolean;
}> = ({ value, onCommit, placeholder, live, disabled }) => {
    const [draft, setDraft] = useState(value);
    useEffect(() => setDraft(value), [value]);
    const commit = () => draft !== value && onCommit(draft);
    return (
        <input
            className={cx(inputCls, disabled && 'cursor-not-allowed opacity-60')}
            value={draft}
            placeholder={placeholder}
            disabled={disabled}
            onChange={e => {
                setDraft(e.target.value);
                if (live) onCommit(e.target.value);
            }}
            onBlur={commit}
            onKeyDown={e => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                    setDraft(value);
                    (e.target as HTMLInputElement).blur();
                }
            }}
        />
    );
};

export const NumberInput: React.FC<{
    value: number | null;
    onCommit: (v: number) => void;
    min?: number;
    max?: number;
    step?: number;
    suffix?: string;
    placeholder?: string;
}> = ({ value, onCommit, min, max, step = 1, suffix, placeholder }) => {
    const fmt = (v: number | null) => (v === null || !Number.isFinite(v) ? '' : String(Math.round(v * 100) / 100));
    const [draft, setDraft] = useState(fmt(value));
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        if (!focused) setDraft(fmt(value));
    }, [value, focused]);

    const clamp = (v: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));
    const commit = (raw: string) => {
        const v = parseFloat(raw);
        if (Number.isFinite(v)) onCommit(clamp(v));
        else setDraft(fmt(value));
    };

    return (
        <div className="relative w-full">
            <input
                className={cx(inputCls, suffix && 'pr-7')}
                inputMode="decimal"
                value={draft}
                placeholder={placeholder ?? (value === null ? 'Mixed' : '')}
                onFocus={() => setFocused(true)}
                onChange={e => setDraft(e.target.value)}
                onBlur={() => {
                    setFocused(false);
                    commit(draft);
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        const base = parseFloat(draft);
                        const delta = (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
                        const v = clamp((Number.isFinite(base) ? base : 0) + delta);
                        setDraft(fmt(v));
                        onCommit(v);
                    }
                }}
            />
            {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">{suffix}</span>}
        </div>
    );
};

export const SelectInput = <T extends string>({
    value,
    options,
    onChange,
}: {
    value: T;
    options: { value: T; label: string }[];
    onChange: (v: T) => void;
}) => (
    <Select aria-label="Select" selectedKeys={[String(value ?? '')]} onChange={e => onChange(e.target.value as T)}>
        {options.map(o => (
            <SelectItem key={String(o.value)}>{o.label}</SelectItem>
        ))}
    </Select>
);

export const ColorInput: React.FC<{ value: string; onChange: (v: string) => void; title?: string; disabled?: boolean }> = ({ value, onChange, title, disabled }) => (
    <input
        type="color"
        title={title}
        disabled={disabled}
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#cccccc'}
        onChange={e => onChange(e.target.value)}
        className="h-7 w-8 shrink-0 cursor-pointer rounded border disabled:cursor-not-allowed disabled:opacity-60 border-gray-300 bg-white p-0.5 dark:border-nightBorder dark:bg-nightElevated"
    />
);

export const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
    <label className="flex items-center justify-between text-xs text-gray-600 dark:text-nightTextSecondary cursor-pointer select-none">
        <span>{label}</span>
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className={cx(
                'relative h-4 w-7 rounded-full transition-colors',
                checked ? 'bg-primary40 dark:bg-nightAccent' : 'bg-gray-300 dark:bg-nightBorder',
            )}
        >
            <span className={cx('absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', checked ? 'left-3.5' : 'left-0.5')} />
        </button>
    </label>
);

export const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'danger' | 'ghost' }> = ({
    variant = 'default',
    className,
    ...props
}) => (
    <button
        type="button"
        {...props}
        className={cx(
            'inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded text-xs font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none',
            variant === 'primary' && 'bg-primary40 text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg',
            variant === 'danger' && 'text-error40 border border-error80 hover:bg-error95 dark:border-error30 dark:hover:bg-error10',
            variant === 'default' && 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-nightBorder dark:bg-nightElevated dark:text-nightTextSecondary dark:hover:bg-nightSurface',
            variant === 'ghost' && 'text-gray-600 hover:bg-gray-100 dark:text-nightTextSecondary dark:hover:bg-nightElevated',
            className,
        )}
    />
);

export const IconButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean; shortcut?: string; label: string }> = ({
    active,
    shortcut,
    label,
    className,
    ...props
}) => (
    <button
        type="button"
        aria-label={label}
        title={shortcut ? `${label} (${shortcut})` : label}
        {...props}
        className={cx(
            'inline-flex h-8 w-8 items-center justify-center rounded transition-colors disabled:opacity-30 disabled:pointer-events-none',
            active
                ? 'bg-primary99 text-primary30 ring-1 ring-primary80 dark:bg-nightAccent/15 dark:text-nightAccent dark:ring-nightAccent/40'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-nightTextSecondary dark:hover:bg-nightElevated',
            className,
        )}
    />
);

export const Divider: React.FC = () => <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-nightBorder" />;

export const Choice: React.FC<{ active: boolean; title: string; onClick: () => void; children: React.ReactNode }> = ({ active, title, onClick, children }) => (
    <button
        type="button"
        title={title}
        aria-label={title}
        aria-pressed={active}
        onClick={onClick}
        className={cx(
            'h-8 w-8 shrink-0 flex items-center justify-center rounded border transition-colors',
            active
                ? 'border-primary40 bg-primary40 text-white dark:border-nightAccent dark:bg-nightAccent dark:text-nightBg'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-nightBorder dark:text-nightTextSecondary dark:hover:bg-nightElevated',
        )}
    >
        {children}
    </button>
);

export { cx };
